import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadMealIdeas } from '../../../redux/actions/mealIdeas.js';
import { listKeyForId } from '../../../redux/food/normalize.js';
import { selectMealIdeasLoaded, selectMealIdeasLoading } from '../../../redux/food/selectors.js';
import useNowMinute from '../../hooks/useNowMinute.js';
import useGroceryListDraft from '../../hooks/useGroceryListDraft.js';


import CircleLoader from '../CircleLoader';
import MaterialIcon from '../../elements/MaterialIcon.jsx';
import GroceryListArchive from '../GroceryListArchive.jsx';
import GroceryListDisplay, { BoughtListDisplay } from "./GroceryListDisplay.jsx";

/**
 * The Groceries tab, and the column beside the calendar: the budget's active list, the running list of
 * what needs buying. A bought list opened from the calendar shows here in its place until it is closed.
 *
 * Both come from the food store: the active list as a draft over its rows (useGroceryListDraft), so
 * a refresh landing while a row is typed in leaves the row, its text and its focus alone.
 *
 * The History button in the heading's corner swaps the list for the shopping history, every bought
 * list (GroceryListArchive), with a back arrow to the list. That used to be a tab of its own.
 */
export default function GroceryListEdit() {
	const dispatch = useDispatch();
	const minute = useNowMinute();
	const draft = useGroceryListDraft(minute);
	const openedId = useSelector((state) => state.calendar.lastSelectedGroceryList);
	const opened = useSelector((state) => {
		const key = openedId ? listKeyForId(state.food.entities, openedId) : null;
		const list = key ? state.food.entities.groceryLists[key] : null;
		return list && list.purchased_on ? list : null;
	});
	const mealIdeasLoaded = useSelector(selectMealIdeasLoaded);
	const mealIdeasLoading = useSelector(selectMealIdeasLoading);
	const [showingHistory, setShowingHistory] = useState(false);

	// An item from a meal idea links to it by name, which only the meal ideas store knows. Without
	// this the links appeared only after the Meal Ideas tab had been opened once.
	useEffect(() => {
		if(!mealIdeasLoaded && !mealIdeasLoading) {
			dispatch(loadMealIdeas());
		}
	}, []);

	if(showingHistory) {
		return (
			<React.Fragment>
				<div className='grocery_list_header'>
					<button
						type='button'
						className='btn-flat grocery_list_header__back'
						onClick={() => setShowingHistory(false)}
					>
						<MaterialIcon>arrow_back</MaterialIcon>
						Groceries
					</button>
				</div>
				<GroceryListArchive />
			</React.Fragment>
		);
	}

	return (
		<React.Fragment>
			<div className='grocery_list_header'>
				<h5 className='grocery_list_heading'>Groceries</h5>
				<button
					type='button'
					className='btn-flat grocery_list_header__history'
					title='Shopping history'
					onClick={() => setShowingHistory(true)}
				>
					<MaterialIcon>history</MaterialIcon>
					History
				</button>
			</div>
			{!!opened &&
			<BoughtListDisplay key={opened.key} list={opened} />
			}
			{!opened && !draft &&
			<CircleLoader />
			}
			{!opened && !!draft &&
			<GroceryListDisplay
				key={draft.id}
				groceryList={draft}
			/>
			}
		</React.Fragment>
	);
}
